import type {
  SubscriptionSpecOptOutType,
  SubscriptionStatus,
  TextMatchOperator,
} from "Types/beRecipientsApi";

import type { LabelValuePair, LabelValueStringPair } from "Types/common";

//

export type ChannelSubscriptionStatus =
  | "SUBSCRIBED"
  | "PENDING"
  | "UNSUBSCRIBED"
  | undefined;

export type Identifier =
  | undefined
  | "Email"
  | "MobileNumber"
  | "MobilePrefix"
  | `Field${number}`;

export interface CriterionUndefined {
  critType: "undefined";
}

export interface CriterionAnagraphicField {
  critType: "anagraphicField";
  identifier: Identifier;
  matchOperator: TextMatchOperator | undefined;
  value: string;
}

export interface CriterionGroups {
  critType: "groups";
  matchOperator: "BelongsTo" | "NotBelongsTo";
  values: number[];
}

export interface CriterionOptinDate {
  critType: "optinDate";
  channel: "email" | "sms";
  datesType: "beforeDate" | "afterDate" | "interval" | undefined;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

export interface CriterionOptoutDate {
  critType: "optoutDate";
  channel: "email" | "sms";
  datesType: "beforeDate" | "afterDate" | "interval" | undefined;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

export interface CriterionPendingDate {
  critType: "pendingDate";
  channel: "email";
  datesType: "beforeDate" | "afterDate" | "interval" | undefined;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

export interface CriterionOptoutType {
  critType: "optoutType";
  channel: "email" | "sms";
  optoutType: SubscriptionSpecOptOutType | undefined;
}

export interface CriterionOptoutMessage {
  critType: "optoutMessage";
  idMessage: number | undefined;
}

export interface CriterionFilter {
  critType: "filters";
  mode: "And" | "Or";
  values: number[];
}

export type Criterion =
  | CriterionUndefined
  | CriterionAnagraphicField
  | CriterionGroups
  | CriterionOptinDate
  | CriterionOptoutDate
  | CriterionPendingDate
  | CriterionOptoutType
  | CriterionOptoutMessage
  | CriterionFilter;

export type CritType =
  | "undefined"
  | "anagraphicField"
  | "groups"
  | "optinDate"
  | "optoutDate"
  | "pendingDate"
  | "optoutType"
  | "optoutMessage"
  | "filters";
export interface SearchFilters {
  contact: string;
  status: SubscriptionStatus[];
  criteria: Criterion[];
}

export const DefaultCriterionUndefined: CriterionUndefined = {
  critType: "undefined",
};

export const DefaultCriterionAnagraphicField: CriterionAnagraphicField = {
  critType: "anagraphicField",
  identifier: undefined,
  matchOperator: undefined,
  value: "",
};

export const DefaultCriterionGroups: CriterionGroups = {
  critType: "groups",
  matchOperator: "BelongsTo",
  values: [],
};

export const DefaultCriterionOptinDate: CriterionOptinDate = {
  critType: "optinDate",
  channel: "email",
  datesType: undefined,
  dateFrom: undefined,
  dateTo: undefined,
};

export const DefaultCriterionOptoutDate: CriterionOptoutDate = {
  critType: "optoutDate",
  channel: "email",
  datesType: undefined,
  dateFrom: undefined,
  dateTo: undefined,
};

export const DefaultCriterionPendingDate: CriterionPendingDate = {
  critType: "pendingDate",
  channel: "email",
  datesType: undefined,
  dateFrom: undefined,
  dateTo: undefined,
};

export const DefaultCriterionOptoutType: CriterionOptoutType = {
  critType: "optoutType",
  channel: "email",
  optoutType: undefined,
};

export const DefaultCriterionOptoutMessage: CriterionOptoutMessage = {
  critType: "optoutMessage",
  idMessage: undefined,
};

export const DefaultCriterionFilter: CriterionFilter = {
  critType: "filters",
  mode: "And",
  values: [],
};

export const critTypeDefaultMap: Record<CritType, Criterion> = {
  undefined: DefaultCriterionUndefined,
  anagraphicField: DefaultCriterionAnagraphicField,
  groups: DefaultCriterionGroups,
  optinDate: DefaultCriterionOptinDate,
  optoutDate: DefaultCriterionOptoutDate,
  pendingDate: DefaultCriterionPendingDate,
  optoutType: DefaultCriterionOptoutType,
  optoutMessage: DefaultCriterionOptoutMessage,
  filters: DefaultCriterionFilter,
};

//

export const isCriterionAnagraphicField = (
  c: Criterion,
): c is CriterionAnagraphicField => {
  return c.critType === "anagraphicField";
};

export const isCriterionGroups = (c: Criterion): c is CriterionGroups => {
  return c.critType === "groups";
};

export const isCriterionOptinDate = (c: Criterion): c is CriterionOptinDate => {
  return c.critType === "optinDate";
};

export const isCriterionOptoutDate = (
  c: Criterion,
): c is CriterionOptoutDate => {
  return c.critType === "optoutDate";
};

export const isCriterionPendingDate = (
  c: Criterion,
): c is CriterionPendingDate => {
  return c.critType === "pendingDate";
};

export const isCriterionOptoutType = (
  c: Criterion,
): c is CriterionOptoutType => {
  return c.critType === "optoutType";
};

export const isCriterionOptoutMessage = (
  c: Criterion,
): c is CriterionOptoutMessage => {
  return c.critType === "optoutMessage";
};

export const isTextMatchOperatorSingleValue = (
  op: TextMatchOperator | undefined,
): boolean => {
  return (
    op !== undefined &&
    [
      "TextPrefix",
      "TextNotPrefix",
      "TextSuffix",
      "TextNotSuffix",
      "TextContains",
      "TextNotContains",
      "TextSame",
      "TextGreater",
      "TextGreaterEqual",
      "TextLess",
      "TextLessEqual",
      "TextDifferent",
    ].includes(op)
  );
};

export const isTextMatchOperatorMultipleValue = (
  op: TextMatchOperator | undefined,
): boolean => {
  return op !== undefined && ["TextIn", "TextNotIn"].includes(op);
};

export const isCriterionAnagraphicFieldValid = (
  c: CriterionAnagraphicField,
): boolean => {
  const isSingleValue = isTextMatchOperatorSingleValue(c.matchOperator);
  const isMultiValue = isTextMatchOperatorMultipleValue(c.matchOperator);

  const isValueValid =
    isSingleValue || isMultiValue ? c.value.length > 0 : true;

  return (
    c.identifier !== undefined && c.matchOperator !== undefined && isValueValid
  );
};

export const isCriterionGroupsValid = (c: CriterionGroups): boolean => {
  return c.matchOperator !== undefined && c.values.length > 0;
};

export const isCriterionDatesValid = (
  c: CriterionOptinDate | CriterionOptoutDate | CriterionPendingDate,
): boolean => {
  const validDateFrom =
    c.datesType === "afterDate" || c.datesType === "interval"
      ? c.dateFrom !== undefined
      : true;

  const validDateTo =
    c.datesType === "beforeDate" || c.datesType === "interval"
      ? c.dateTo !== undefined
      : true;

  return (
    c.channel !== undefined &&
    c.datesType !== undefined &&
    validDateFrom &&
    validDateTo
  );
};

export const isCriterionOptoutTypeValid = (c: CriterionOptoutType): boolean => {
  return c.channel !== undefined && c.optoutType !== undefined;
};

export const isCriterionOptoutMessageValid = (
  c: CriterionOptoutMessage,
): boolean => {
  return c.idMessage !== undefined;
};

export const isCriterionFilter = (c: Criterion): c is CriterionFilter => {
  return c.critType === "filters";
};

export const isCriterionFilterValid = (c: CriterionFilter): boolean => {
  return c.mode !== undefined && c.values.length > 0;
};

export const isCriteriaValid = (criteria: Criterion[]): boolean => {
  return criteria.reduce((acc, c) => {
    if (isCriterionAnagraphicField(c)) {
      return acc && isCriterionAnagraphicFieldValid(c);
    }

    if (isCriterionGroups(c)) {
      return acc && isCriterionGroupsValid(c);
    }

    if (
      isCriterionOptinDate(c) ||
      isCriterionOptoutDate(c) ||
      isCriterionPendingDate(c)
    ) {
      return acc && isCriterionDatesValid(c);
    }

    if (isCriterionOptoutType(c)) {
      return acc && isCriterionOptoutTypeValid(c);
    }

    if (isCriterionOptoutMessage(c)) {
      return acc && isCriterionOptoutMessageValid(c);
    }

    if (isCriterionFilter(c)) {
      return acc && isCriterionFilterValid(c);
    }

    return acc;
  }, true);
};

//

export type ColumnName =
  | "Id"
  | "EmailSubscriptionStatus" //
  //  | 'EmailPending'  ?
  //  | 'EmailUnsubscribed'
  //  | 'MobileSubscribed'
  //  | 'MobilePending'
  //  | 'MobileUnsubscribed'
  | "Email"
  | "Mobile" //
  | "MobilePrefix"
  | "MobileNumber"
  | "Engagement"
  | "Groups"
  | "RegistrationDate"
  | "EmailPendingDate"
  | "EmailSubscriptionDate"
  | "EmailUnsubscriptionDate"
  | "SMSSubscriptionStatus" //
  | "SMSSubscriptionDate"
  | "SMSUnsubscriptionDate"
  //
  | "ReasonEmail"
  | "ReasonSms"
  | "ConfirmedOptin"
  //
  | "TrackingState"
  | "TrackingLastChange"
  | `Field${number}`;

export interface PagePreferences {
  status: SubscriptionStatus[];
  columns: ColumnName[];
  modalUnsubscribe: {
    channelEmail: boolean;
    channelSMS: boolean;
  };
  modalBulkUnsubscribe: {
    channelEmail: boolean;
    channelSMS: boolean;
  };
  groups: {
    orderBy: "IdGroup" | "GroupName" | "AssignmentDate";
    orderDescending: boolean;
  };
}

export interface PaginationState {
  pageNumber: number;
  pageSize: number;
  totalItems: number;
  orderBy: ColumnName;
  orderDescending: boolean;
  loading: boolean;
  forcedRecipient: boolean;
}

export type SearchFiltersState = {
  [K in keyof SearchFilters]: {
    value: SearchFilters[K];
    dirty: boolean;
  };
};
export const defaultSearchStatus: SubscriptionStatus[] = [
  "EMAILSUBSCRIBED",
  "MOBILESUBSCRIBED",
];

export const defaultColumns: ColumnName[] = [
  "Email",
  "Engagement",
  "EmailSubscriptionStatus",
  "EmailSubscriptionDate",
  "Mobile",
  "SMSSubscriptionStatus",
  "SMSSubscriptionDate",
  "TrackingState",
  "TrackingLastChange",
  "Field1",
  "Field2",
  "Field3",
];

export const defaultPagePreferences: PagePreferences = {
  status: defaultSearchStatus,
  columns: defaultColumns,
  modalUnsubscribe: {
    channelEmail: false,
    channelSMS: false,
  },
  modalBulkUnsubscribe: {
    channelEmail: false,
    channelSMS: false,
  },
  groups: {
    orderBy: "AssignmentDate",
    orderDescending: true,
  },
};

export interface ResultField {
  id: number;
  value: string;
}

export interface DynamicField {
  id: number;
  description: string;
  masked: boolean;
}

export interface OptoutConditionsCountSend {
  Condition: "OPTCOND_COUNTSEND";
  CountSend: number;
}

export interface OptoutConditionsSendInterval {
  Condition: "OPTCOND_SENDINTERVAL";
  SendInterval: number;
}

export interface OptoutConditionsDifferentEmail {
  Condition: "OPTCOND_DIFFERENTEMAIL";
  DifferentEmail: string;
}

export interface OptoutConditionsDateRange {
  Condition: "OPTCOND_DATERANGE";
  DateFrom: string;
  DateTo: string;
}

export const isOptoutConditionsCountSend = (
  oc: OptoutConditions | undefined,
): oc is OptoutConditionsCountSend => oc?.Condition === "OPTCOND_COUNTSEND";

export const isOptoutConditionsSendInterval = (
  oc: OptoutConditions | undefined,
): oc is OptoutConditionsSendInterval =>
  oc?.Condition === "OPTCOND_SENDINTERVAL";

export const isOptoutConditionsDifferentEmail = (
  oc: OptoutConditions | undefined,
): oc is OptoutConditionsDifferentEmail =>
  oc?.Condition === "OPTCOND_DIFFERENTEMAIL";

export const isOptoutConditionsDateRange = (
  oc: OptoutConditions | undefined,
): oc is OptoutConditionsDateRange => oc?.Condition === "OPTCOND_DATERANGE";

export type OptoutConditions =
  | OptoutConditionsCountSend
  | OptoutConditionsSendInterval
  | OptoutConditionsDifferentEmail
  | OptoutConditionsDateRange;

export interface SearchResult {
  Id: number;
  Email: string;
  MobilePrefix: string;
  MobileNumber: string;
  Mobile: string;
  Fields: ResultField[];
  RegistrationDate?: string;
  EmailSubscriptionStatus: ChannelSubscriptionStatus;
  EmailSubscriptionDate?: string;
  EmailSubscriptionDateExt?: string;
  EmailPendingDate?: string;
  EmailPendingDateExt?: string;
  EmailPendingDateHMS?: string;
  EmailUnsubscriptionDate?: string;
  EmailUnsubscriptionDateExt?: string;
  ReasonEmail?: SubscriptionSpecOptOutType;
  EmailConfirmationDateExt?: string;
  EmailConfirmationIP?: string;
  EmailSubscriptionRequestIP?: string;
  ConfirmedOptin?: boolean;
  SMSSubscriptionStatus: ChannelSubscriptionStatus;
  SMSSubscriptionDate?: string;
  SMSSubscriptionDateExt?: string;
  SMSUnsubscriptionDate?: string;
  SMSUnsubscriptionDateExt?: string;
  ReasonSms?: SubscriptionSpecOptOutType;
  TrackingState?: boolean;
  TrackingLastChange?: string;
  SMSConfirmationIP?: string;
  SMSSubscriptionRequestIP?: string;

  optoutConditions?: OptoutConditions;

  actions: {
    profile: boolean;
    edit: boolean;
    unsubscribe: boolean;
    delete: boolean;
    sendConfirmationEmail: boolean;
    canChangeEmailStatus: boolean;
    canChangeSmsStatus: boolean;
  };
}

export interface PageParams {
  se?: string; // search Email
  spp?: string; // search PhonePrefix
  spn?: string; // search PhoneNumber
  sig?: string; // search Include Groups
  sig2?: string; //  search additional Include Groups
  seg?: string; // search Exclude Groups
  saf?: string; // search Apply Filters
  safo?: string; // search Apply Filters Or
  smf?: string; // search MatchFields
  smfv?: string; // strSearchMatchFieldsValues
  ssf?: string; // strSearchSubscribedFrom, strSearchReqSubscribedFrom
  sst?: string; // strSearchSubscribedTo, strSearchReqSubscribedTo
  suf?: string; // strSearchUnsubscribedFrom
  sut?: string; // strSearchUnsubscribedTo
  sote?: string; // search OptOutType Email
  sots?: string; // search OptOutType Sms
  som?: string; // search OptOut Message
  sch?: "email" | "sms"; //  NEW search channel
  ssst?: "optin" | "pending" | "optout"; // NEW search subscription status
  rid?: string; // recipient id to edit on page access
  nr?: string; // new recipient open on page access
}

export interface IdentifiersListState {
  items: Array<{ value: Identifier; label: string }>;
  loaded: false;
}

export interface ModalUnsubscribeState {
  show: boolean;
  idRecipient: number | undefined;
  channelEmail: boolean;
  channelSMS: boolean;
  wipeData: boolean;
  //
  lists: number[];
  allLists: boolean;
  recipientSubscriptionLists: number[];
}

export interface ModalUnsubscribePreferences {
  channelEmail: boolean;
  channelSMS: boolean;
}

export interface ModalBulkUnsubscribePreferences {
  channelEmail: boolean;
  channelSMS: boolean;
}

export interface ModalDeleteState {
  show: boolean;
  idRecipient: number | undefined;
  mode: "delete" | "unsubscribe" | undefined;
}

export interface ModalColumnsState {
  show: boolean;
  items: Array<ColumnName | undefined>;
}

export interface ModalFilterDetailsState {
  show: boolean;
  id?: number;
  type?:
    | "Anagraphic"
    | "Activity"
    | "Device"
    | "Geolocation"
    | "MultiFilter"
    | "MultiType"
    | "Tracking"
    | "Engagement";
  description?: string;
  name?: string;
}

export const editableFiltersTypes: Array<ModalFilterDetailsState["type"]> = [
  "Activity",
  "Anagraphic",
  "Engagement",
  "Tracking",
  "Geolocation",
];

export interface ModalDeliverabilityIndexContactState {
  show: boolean;
  email: string;
  text: string;
}

export interface Recipient {
  Id: number;
  Email: string;
  Mobile: string;
  Fields: ResultField[];
  EmailSubscriptionStatus: ChannelSubscriptionStatus;
  SMSSubscriptionStatus: ChannelSubscriptionStatus;
}

export interface PanelEditRecipientState {
  show: boolean;
  title?: string;
  recipient?: SearchResult;
  score?: EngagementScore;
  tracking?: boolean;
  trackingInsertDate?: string;
  idNLFrom?: number;
}

export interface SelectionState {
  mode: "all" | "selection";
  checked: number[];
  unchecked: number[]; //  future
}

export interface ModalBulkUnsubscribeState {
  show: boolean;
  channelEmail: boolean;
  channelSMS: boolean;
  wipeData: boolean;
}

export interface ModalBulkAddToGroupState {
  show: boolean;
  addToGroupId: number | undefined;
  isAddToGroupIdDirty: boolean;
  optRemoveFromGroups: boolean;
  removeFromGroupsMode: "all" | "search";
  optMoveRandom: boolean;
  moveRandomAmount: number | undefined;
  isMoveRandomAmountDirty: boolean;
  optSetAsTestGroup: boolean;
  searchGroups: number[];
}

export interface ModalBulkRemoveFromGroupsState {
  show: boolean;
  addToGroupId: number | undefined;
  isAddToGroupIdDirty: boolean;
  removeFromGroupsMode: "all" | "search";
  moveRandomAmount: number | undefined;
  isMoveRandomAmountDirty: boolean;
  optMoveRandom: boolean;
  optAddToGroup: boolean;
  optSetAsTestGroup: boolean;
  searchGroups: number[];
}

export interface ModalSaveSegmentState {
  show: boolean;
  name: string;
  nameIsDirty: boolean;
}

export interface GroupInfo {
  Id: number;
  Name: string;
  AssignmentDate: string;
}

export interface RecipientGroupsState {
  loading: boolean;
  status: "initial" | "ready" | "error";
  items: GroupInfo[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
  orderBy: "IdGroup" | "GroupName" | "AssignmentDate";
  orderDescending: boolean;
}

export interface TrackingChangeLogItem {
  IdLog: number;
  InsertDate: string;
  OpenTrackingEnabled: boolean;
  IdNLFrom: number;
}

export interface RecipientTrackingChangeLogsState {
  loading: boolean;
  status: "initial" | "ready" | "error";
  items: TrackingChangeLogItem[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
  orderBy: "InsertDate" | "IdLog";
  orderDescending: boolean;
}

export interface GroupsPreferences {
  orderBy: "IdGroup" | "GroupName" | "AssignmentDate";
  orderDescending: boolean;
}

export interface ListsState {
  items: LabelValuePair[];
  loaded: boolean;
}

/* Modal find & replace */

export type FindReplaceField =
  | "Email"
  | "MobilePrefix"
  | "MobileNumber"
  | `Field${number}`;

export type FindReplaceMode =
  | "Contains"
  | "Equals"
  | "StartsWith"
  | "EndsWith"
  | "All";

export interface FindReplaceItem {
  id: number;
  oldValue: string;
  newValue: string;
}

export interface ModalBulkFindReplaceState {
  show: boolean;
  FindColumn?: FindReplaceField;
  FindText: string;
  ReplaceText: string;
  FindCaseSensitive: boolean;
  FindType: FindReplaceMode;

  isFindTextDirty: boolean;
  isReplaceTextDirty: boolean;

  previewStatus: "initial" | "fetching" | "paging" | "ready" | "error";
  //  snapshot of preview params
  previewFindText: string;
  previewFindCaseSensitive: boolean;
  previewReplaceText: string;
  previewFindType: FindReplaceMode;

  idTask?: number;
  pollingIteration: number;

  items: FindReplaceItem[];
  pageNumber: number;
  pageSize: number;
  totalItems: number;
  //
  EmptyField?: `Field${number}`;
  isEmptyFieldDirty: boolean;
}

export interface ModalPendingState {
  show: boolean;
  idRecipient: number;
  contact: SearchResult["Email"];
  idConfirmationMessage: number;
  idConfirmationMessageIsDirty: boolean;
}

export interface ModalSendConfirmationEmailState {
  show: boolean;
  idRecipient: number;
  contact: SearchResult["Email"];
  idConfirmationMessage: number;
  idConfirmationMessageIsDirty: boolean;
}

export interface ModalSubscribeState {
  show: boolean;
  idRecipient: number;
  contactEmail: SearchResult["Email"];
  contactSMS: SearchResult["Mobile"];
  //
  lists: number[];
  allLists: boolean;
  recipientSubscriptionLists: number[];
  listsIsDirty: boolean;
  //
  channelEmail: boolean;
  channelSMS: boolean;
  sendConfirmation: boolean;
  idConfirmationMessage: number;
  idConfirmationMessageIsDirty: boolean;
  forceSubscription: boolean;
}

export type EngagementScore =
  | "New"
  | "Inactive"
  | "Abandoned"
  | "LessActive"
  | "Active"
  | "VeryActive";

export interface EngagementItem {
  idRecipient: number;
  score: EngagementScore;
}

export interface RecipientsEngagementState {
  status: "initial" | "loading" | "ready" | "error";
  items: EngagementItem[];
}

export interface RecipientsGroupsState {
  status: "initial" | "loading" | "ready" | "error";
  items: RecipientGroupsItem[];
}

export interface RecipientGroupsItem {
  idRecipient: number;
  moreItems: boolean;
  items: GroupInfo[];
}

export interface ModalRecipientGroupsState {
  show: boolean;
  idRecipient: number;
  contact: string;
  loading: boolean;
  status: "initial" | "ready" | "error";
  items: GroupInfo[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
  orderBy: "IdGroup" | "GroupName" | "AssignmentDate";
  orderDescending: boolean;
}

export interface NewRecipient {
  Email: string;
  MobilePrefix: string;
  MobileNumber: string;
  Fields: ResultField[];
  Groups: number[];
}
export interface PanelAddRecipientState {
  show: boolean;
  recipient: NewRecipient;
  isDirty: boolean;
  sendConfirmation: boolean;
}

export interface PanelAddRecipientGroupsPagingState {
  loading: boolean;
  items: GroupInfo[];
  pageNumber: number;
  pageSize: number;
  orderBy: "Id" | "Name";
  orderDescending: boolean;
}

export interface MobilePrefixesState {
  loaded: boolean;
  items: LabelValueStringPair[];
}

export interface ModalDelStatsContactState {
  show: boolean;
  email: string;
  isDirty: boolean;
  requestTitle: string;
  requestDescription: string;
}

export interface PanelEditContactsState {
  show: boolean;
  idRecipient: number;
  isDirty: boolean;
  EmailDisabled: boolean;
  EmailRequired: boolean;
  SMSDisabled: boolean;
  SMSRequired: boolean;
  Email: string;
  MobilePrefix: string;
  MobileNumber: string;
}

export type ActivityStreamFilterType =
  | "subscription"
  | "unsubscription"
  | "bounces"
  | "clicks"
  | "sendings"
  | "opens";

export const allEmailActivityStreamFilters: ActivityStreamFilterType[] = [
  "subscription",
  "unsubscription",
  "bounces",
  "clicks",
  "sendings",
  "opens",
];

export const allSmsActivityStreamFilters: ActivityStreamFilterType[] = [
  "subscription",
  "unsubscription",
  "clicks",
  "sendings",
];

export interface RecipientActivityStreamFiltersState {
  filters: ActivityStreamFilterType[];
  allFilters: boolean;
}

export interface ModalRecipientTrackingState {
  show: boolean;
  idRecipient: number;
  tracking: boolean;
}

export interface ModalAddSegmentState {
  show: boolean;
  name: string | null;
  nameConflict: boolean;
  maxRecipientsInGroup: number | null;
  distMode: "Random" | "Date";
  recipientsCount: number;
  //
  searchCrit: SearchFiltersState;
}

export const MAX_SEGMENTATION_GROUPS = 100;

export interface PanelConfigColumnsState {
  show: boolean;
  items: Array<ColumnName | undefined>;
}

export const MAX_RECIPIENTS_EXPORT_XLSX = 1_000_000;
